import { Divider, Step, StepLabel, StepLabelProps, Stepper, StepProps } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { AppBox, AppButton, AppDialog, AppDialogRefType, AppIcon, AppTypography } from 'components'
import { ApiQueryKeys, SubAccountStatus } from 'globals/constants'
import { useSnackbar } from 'notistack'
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { AccountApi, SubAccountViewModel } from 'sdk'
import { RemoveSubAccountStepThreeConfirmation } from './RemoveSubAccountStepThreeConfirmation'
import { RemoveSubAccountStepTwoVerify } from './RemoveSubAccountStepTwoVerify'

interface Props {
  onRemoveSuccess: () => void
  onStatusChange: (_status: SubAccountStatus, subAccountId: string) => void
}
export interface RemoveSubAccountDialogRefType {
  openDialog?: (subAccount: SubAccountViewModel) => void
}

export const RemoveSubAccountDialog = forwardRef((props: Props, ref) => {
  const accountApi = new AccountApi()
  const { enqueueSnackbar } = useSnackbar()
  const steps = ['Remove', 'Verify', 'Confirmation']
  const [activeStep, setActiveStep] = useState(1)
  const [subAccount, setSubAccount] = useState<SubAccountViewModel>()
  const [agreementAccepted, setAgreementAccepted] = useState(false)
  const [newAccess, setNewAccess] = useState<SubAccountStatus>()
  const dialogRef = useRef<AppDialogRefType>({})

  const { mutate: remove, isPending: isRemoving } = useMutation({
    mutationKey: [ApiQueryKeys.account.removeSubAccountFromBilling],
    mutationFn: () => accountApi.removeSubAccountFromBilling(subAccount!.Id),
    onError: (error) => enqueueSnackbar(error.message, { variant: 'error' }),
    onSuccess: () => {
      setActiveStep(activeStep + 1)
      props.onRemoveSuccess()
    },
  })

  const renderStepsContent = () => {
    switch (activeStep) {
      case 1:
        return (
          <RemoveSubAccountStepTwoVerify
            subAccount={subAccount!}
            agreementAccepted={agreementAccepted}
            setAgreementAccepted={setAgreementAccepted}
          />
        )
      case 2:
        return <RemoveSubAccountStepThreeConfirmation subAccount={subAccount!} onAccessChange={setNewAccess} />
      default:
        return null
    }
  }
  const nextButtonLabel = () => {
    switch (activeStep) {
      case 1:
        return 'Confirm'
      case 2:
        return 'Close'
    }
  }

  const handleContinueClick = () => {
    if (activeStep === 1) {
      remove()
    } else if (activeStep === 2) {
      if (newAccess) {
        props.onStatusChange(newAccess, subAccount!.Id)
      }
      setActiveStep(1)
      dialogRef.current.closeDialog!()
      return
    }
  }
  const handleBackClick = () => {
    if (activeStep === 1) {
      dialogRef.current.closeDialog!()
      return
    }
  }

  const isNextBtnDisabled = () => {
    if (activeStep === 1) {
      return !agreementAccepted
    }
    return false
  }
  const title = useMemo(() => {
    if (activeStep === 1) {
      return 'Manage Your ParkSitesIO User'
    } else {
      return 'Manage Your ParkSitesIO Subscription Plan'
    }
  }, [activeStep])

  const openDialog = (subAccount: SubAccountViewModel) => {
    setSubAccount(subAccount)
    dialogRef.current.openDialog!()
  }
  if (ref) {
    ;(ref as any).current = { openDialog }
  }
  useEffect(() => {
    setNewAccess(undefined)
    setAgreementAccepted(false)
  }, [subAccount])
  return (
    <AppDialog
      ref={dialogRef}
      title={title}
      hideCancelButton={true}
      hideFooterCloseBtn={true}
      hideTitleBottomBorder={true}
      maxWidth={'md'}
    >
      <AppBox paddingBottom={0}>
        <Stepper
          activeStep={activeStep}
          connector={<Divider sx={{ width: 40, marginX: 0.5, borderWidth: 1, borderColor: 'primary.600' }} />}
          sx={{ justifyContent: 'center' }}
        >
          {steps.map((step, index) => {
            const stepProps: StepProps = {}
            const labelProps: StepLabelProps = {}
            return (
              <Step key={step} {...stepProps}>
                <StepLabel
                  icon={
                    <AppBox
                      width={25}
                      height={25}
                      backgroundColor="primary.100"
                      borderRadius={12}
                      textAlign={'center'}
                      justifySelf={'center'}
                      paddingTop={0.2}
                    >
                      {activeStep > index ? (
                        <AppIcon icon={'material-symbols:check'} size="medium" color="primary.600" />
                      ) : (
                        <AppTypography fontSize={14}>{index + 1}</AppTypography>
                      )}
                    </AppBox>
                  }
                  {...labelProps}
                >
                  <AppTypography fontSize={16} color={'primary.600'} fontWeight={600}>
                    {step}
                  </AppTypography>
                </StepLabel>
              </Step>
            )
          })}
        </Stepper>
      </AppBox>
      <AppBox paddingY={2}>{renderStepsContent()}</AppBox>
      <AppBox>
        <AppBox display={'flex'} gap={2} justifyContent={'flex-end'}>
          {activeStep === 1 && (
            <AppButton onClick={() => handleBackClick()} size="large" color="primary">
              Back
            </AppButton>
          )}
          <AppButton
            variant="contained"
            size="large"
            sidePaddings={6}
            disabled={isNextBtnDisabled()}
            onClick={() => handleContinueClick()}
            isLoading={isRemoving}
          >
            {nextButtonLabel()}
          </AppButton>
        </AppBox>
      </AppBox>
    </AppDialog>
  )
})
